class ForkBridgeConfigurator extends HTMLElement {
  connectedCallback() {
    this.colorButtons = Array.from(this.querySelectorAll('[data-color-button]'));
    this.toggleInputs = Array.from(this.querySelectorAll('[data-engraving-toggle]'));
    this.engravingArea = this.querySelector('[data-engraving-area]');
    this.textInput = this.querySelector('[data-engraving-text]');
    this.previewText = this.querySelector('[data-preview-text]');
    this.previewInstagram = this.querySelector('[data-preview-instagram]');
    this.previewShell = this.querySelector('[data-preview-shell]');
    this.previewColorLabel = this.querySelector('[data-preview-color-label]');
    this.characterCount = this.querySelector('[data-character-count]');
    this.engravingProperty = this.querySelector('[data-engraving-property]');
    this.engravingPositionProperty = this.querySelector('[data-engraving-position-property]');
    this.instagramProperty = this.querySelector('[data-instagram-property]');
    this.addonVariantIdInput = this.querySelector('[data-addon-variant-id]');
    this.addonEnabledInput = this.querySelector('[data-addon-enabled]');
    this.priceNote = this.querySelector('[data-price-note]');
    this.instagramInputs = Array.from(this.querySelectorAll('[data-instagram-toggle]'));
    this.fontInputs = Array.from(this.querySelectorAll('[data-font-toggle]'));
    this.hiddenVariantPicker = this.closest('.product-details')?.querySelector('variant-picker');

    this.colorButtons.forEach((button) => {
      button.addEventListener('click', () => this.handleColorButtonClick(button));
    });

    this.toggleInputs.forEach((input) => {
      input.addEventListener('change', () => this.updateEngravingState());
    });

    this.instagramInputs.forEach((input) => {
      input.addEventListener('change', () => this.updatePreview());
    });

    this.fontInputs.forEach((input) => {
      input.addEventListener('change', () => this.updatePreview());
    });

    this.textInput?.addEventListener('input', () => this.updatePreview());
    this.hiddenVariantPicker?.addEventListener('change', () => this.syncFromVariantPicker());

    this.syncFromVariantPicker();
    this.updateEngravingState();
    this.updatePreview();
  }

  handleColorButtonClick(button) {
    const colorValue = button.dataset.colorValue;
    if (!colorValue) return;
    this.activateColorButton(colorValue);
    this.syncVariantPicker(colorValue);
  }

  activateColorButton(colorValue) {
    const normalized = colorValue.trim().toLowerCase();
    this.colorButtons.forEach((button) => {
      const active = button.dataset.colorValue?.trim().toLowerCase() === normalized;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      if (active) {
        this.previewColorLabel && (this.previewColorLabel.textContent = button.dataset.colorValue || '');
        this.previewShell && button.dataset.colorHandle && (this.previewShell.dataset.color = button.dataset.colorHandle);
      }
    });
  }

  syncVariantPicker(colorValue) {
    if (!this.hiddenVariantPicker) return;
    const select = this.hiddenVariantPicker.querySelector('select');

    if (select instanceof HTMLSelectElement) {
      const option = Array.from(select.options).find((entry) => entry.value.trim().toLowerCase() === colorValue.trim().toLowerCase());
      if (option) {
        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    const radio = Array.from(this.hiddenVariantPicker.querySelectorAll('input[type="radio"]')).find(
      (entry) => entry.value.trim().toLowerCase() === colorValue.trim().toLowerCase(),
    );

    if (radio instanceof HTMLInputElement && !radio.checked) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  syncFromVariantPicker() {
    if (!this.hiddenVariantPicker || this.colorButtons.length === 0) return;
    const select = this.hiddenVariantPicker.querySelector('select');

    if (select instanceof HTMLSelectElement) {
      this.activateColorButton(select.value);
      return;
    }

    const checked = this.hiddenVariantPicker.querySelector('input[type="radio"]:checked');
    if (checked instanceof HTMLInputElement) {
      this.activateColorButton(checked.value);
    }
  }

  updateEngravingState() {
    const enabled = this.toggleInputs.find((input) => input.checked)?.value === 'Ja';
    if (this.engravingArea) {
      this.engravingArea.hidden = !enabled;
      this.engravingArea.classList.toggle('is-hidden', !enabled);
    }
    if (this.textInput) {
      this.textInput.disabled = !enabled;
      this.textInput.required = enabled;
      if (!enabled) this.textInput.value = '';
    }
    if (this.engravingProperty) {
      this.engravingProperty.disabled = !enabled;
      this.engravingProperty.value = enabled ? 'Ja' : 'Nein';
    }
    if (this.engravingPositionProperty) {
      this.engravingPositionProperty.disabled = !enabled;
    }
    if (this.instagramProperty) {
      this.instagramProperty.disabled = !enabled;
    }

    this.instagramInputs.forEach((input) => {
      input.disabled = !enabled;
    });

    this.fontInputs.forEach((input) => {
      input.disabled = !enabled;
    });

    if (!enabled) {
      const defaultInstagramInput = this.instagramInputs.find((input) => input.defaultChecked) || this.instagramInputs[0];
      if (defaultInstagramInput) {
        defaultInstagramInput.checked = true;
      }
    }

    if (this.addonEnabledInput) {
      const addonVariantId = this.addonVariantIdInput?.value?.trim();
      const hasAddon = addonVariantId && addonVariantId !== '__none__';
      this.addonEnabledInput.value = enabled && hasAddon ? 'true' : 'false';
    }

    if (this.priceNote) {
      this.priceNote.classList.toggle('is-hidden', !enabled);
    }

    this.updatePreview();
  }

  updatePreview() {
    const placeholder = this.dataset.previewPlaceholder?.trim() || 'Deine Gravur';
    const enabled = this.toggleInputs.find((input) => input.checked)?.value === 'Ja';
    const value = this.textInput?.value?.trim();
    const text = enabled ? value || placeholder : placeholder;

    if (this.previewText) {
      this.previewText.textContent = text;
      this.previewText.style.opacity = enabled ? '1' : '0.5';
      const selectedFont = this.fontInputs.find((input) => input.checked)?.dataset.previewFont || 'montserrat';
      this.previewText.className = `fork-bridge-preview__text fork-bridge-preview__text--${selectedFont}`;
    }

    const showInstagram = this.instagramInputs.find((input) => input.checked)?.value === 'Ja';
    if (this.previewInstagram) {
      this.previewInstagram.classList.toggle('is-hidden', !showInstagram);
      this.previewInstagram.style.opacity = enabled ? '1' : '0.5';
    }

    if (this.instagramProperty) {
      this.instagramProperty.value = showInstagram ? 'Ja' : 'Nein';
    }

    if (this.characterCount && this.textInput) {
      this.characterCount.textContent = `${this.textInput.value.length} / 20`;
    }
  }
}

if (!customElements.get('fork-bridge-configurator-component')) {
  customElements.define('fork-bridge-configurator-component', ForkBridgeConfigurator);
}
